// src/app/posts/create/[location]/[category]/page.tsx
'use client';

import React, { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useParams, useRouter } from 'next/navigation';
import { useSession } from 'next-auth/react';
import Cookies from 'js-cookie';
import { writeLocationCookie } from '@/lib/location';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { apiPost } from '@/lib/api';
import { createPostError } from './createPostError';

type CreatePostDto = {
  title: string;
  age?: number | null;
  zipCode?: number | null;
  details: string;
  locationSlug: string;
  categorySlug: string;
};

const DETAILS_MAX = 2000;
const TITLE_MAX = 100;

function titleCaseSlug(slug: string): string {
  return slug
    .replace(/-/g, ' ')
    .split(' ')
    .map(w => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ');
}

export default function CreatePostPage() {
  const { location, category } = useParams() as { location: string; category: string };
  const router = useRouter();
  const { data: session, status } = useSession();

  const [title, setTitle] = useState('');
  const [age, setAge] = useState<number | ''>('');
  const [zip, setZip] = useState('');
  const [details, setDetails] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categoryName, setCategoryName] = useState<string | null>(null);

  useEffect(() => {
    // Ensure chooser respects current selection going forward
    try {
      const raw = Cookies.get('userLocation');
      let stateFromCookie = '';
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.state) stateFromCookie = parsed.state;
      }
      // Write BOTH cookies (phoenix + userLocation) so the legacy phoenix cookie
      // never goes stale relative to userLocation. See lib/location.ts.
      writeLocationCookie({ state: stateFromCookie || undefined, metro: location });
    } catch {}
  }, [location]);

  // Resolve the category slug to its friendly display name so the page header
  // doesn't read "FOR-WOMEN" — fall back to title-cased slug if the lookup fails.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/categories?metro=${encodeURIComponent(location)}`, { cache: 'no-store' });
        if (!res.ok) return;
        const list: { slug: string; name: string }[] = await res.json();
        if (cancelled) return;
        const hit = Array.isArray(list) ? list.find(c => c.slug === category) : null;
        if (hit?.name) setCategoryName(hit.name);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [location, category]);

  const placeName = useMemo(() => titleCaseSlug(location), [location]);
  const friendlyCategory = categoryName || titleCaseSlug(category);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!title.trim() || !details.trim()) {
      setError('Please enter a title and details.');
      return;
    }
    setSaving(true);
    try {
      const dto: CreatePostDto = {
        title: title.trim(),
        details: details.trim(),
        locationSlug: location,
        categorySlug: category,
        age: age === '' ? null : Number(age),
        zipCode: zip.trim() ? Number.parseInt(zip.trim(), 10) : null,
      };
      const created = await apiPost<any>(`/api/post`, session, dto);
      const newId = created?.postId ?? created?.postID ?? created?.id;
      if (newId) {
        router.push(`/posts/${location}/${category}/${newId}`);
      } else {
        router.push(`/posts/${location}/${category}`);
      }
    } catch (e: any) {
      setError(createPostError(e));
    } finally {
      setSaving(false);
    }
  }

  if (status === 'loading') return <div className="p-6 text-center">Loading…</div>;
  if (status === 'unauthenticated') {
    const returnTo = `/posts/create/${encodeURIComponent(location)}/${encodeURIComponent(category)}`;
    return (
      <section className="mx-auto max-w-xl px-6 py-12 text-center">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src="/img/JuicyStarz.svg"
          alt="JuicyStarz"
          width={120}
          height={20}
          className="mx-auto mb-4 h-8 w-auto"
          onError={(e) => { (e.currentTarget as HTMLImageElement).src = '/img/JuicyStarz.png'; }}
        />
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">
          Post work and find collaborators in {placeName}
        </h1>
        <p className="mt-3 text-base text-muted-foreground">
          Join JuicyStarz to post in <span className="font-semibold text-foreground">{friendlyCategory}</span>,
          reach local talent and producers, message securely, and get discovered.
        </p>
        <div className="mt-6 flex items-center justify-center gap-3">
          <Button asChild size="lg" variant="primary" className="font-semibold">
            <Link href={`/register?returnTo=${encodeURIComponent(returnTo)}`}>Sign up</Link>
          </Button>
          <Button asChild size="lg" variant="outline">
            <Link href={`/login?returnTo=${encodeURIComponent(returnTo)}`}>Sign in</Link>
          </Button>
        </div>
      </section>
    );
  }

  const detailsRemaining = DETAILS_MAX - details.length;
  const titleRemaining = TITLE_MAX - title.length;

  return (
    <div className="mx-auto max-w-2xl px-4 py-6 sm:py-10">
      <Card>
        <CardHeader className="space-y-2">
          <CardTitle className="text-2xl font-semibold tracking-tight">
            New post in {placeName}
          </CardTitle>
          <CardDescription>{friendlyCategory}</CardDescription>
        </CardHeader>
        <form onSubmit={onSubmit}>
          <CardContent className="space-y-5">
            {error && (
              <div
                role="alert"
                className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              >
                {error}
              </div>
            )}

            <div className="space-y-1.5">
              <div className="flex items-baseline justify-between">
                <Label htmlFor="title">Title</Label>
                <span
                  className={`text-xs ${titleRemaining < 10 ? 'text-destructive' : 'text-muted-foreground'}`}
                >
                  {titleRemaining}
                </span>
              </div>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value.slice(0, TITLE_MAX))}
                placeholder="What's your post about?"
                maxLength={TITLE_MAX}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="age" className="flex items-baseline gap-1">
                  Age <span className="text-xs font-normal text-muted-foreground">(optional)</span>
                </Label>
                <Input
                  id="age"
                  type="number"
                  inputMode="numeric"
                  min={18}
                  value={age}
                  onChange={(e) => setAge(e.target.value === '' ? '' : Number(e.target.value))}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="zip" className="flex items-baseline gap-1">
                  ZIP <span className="text-xs font-normal text-muted-foreground">(optional)</span>
                </Label>
                <Input
                  id="zip"
                  inputMode="numeric"
                  value={zip}
                  onChange={(e) => setZip(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-baseline justify-between">
                <Label htmlFor="details">Details</Label>
                <span
                  className={`text-xs ${detailsRemaining < 100 ? 'text-destructive' : 'text-muted-foreground'}`}
                >
                  {detailsRemaining}
                </span>
              </div>
              <Textarea
                id="details"
                rows={8}
                value={details}
                onChange={(e) => setDetails(e.target.value.slice(0, DETAILS_MAX))}
                placeholder="Describe the work: what you're casting or offering, scene type, dates, location, and rate. Keep contact on-platform, no phone numbers, emails, or off-site handles."
                maxLength={DETAILS_MAX}
                required
              />
            </div>

            <p className="text-xs text-muted-foreground">
              JuicyStarz is for adult-industry production and remote performance. Posts soliciting or offering in-person sexual services or escorting are removed.{' '}
              <Link href="/posts/rules" className="underline hover:no-underline">Posting rules</Link>
            </p>
          </CardContent>
          <CardFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between sm:items-center">
            <Button
              type="button"
              variant="outline"
              onClick={() => router.push('/profile/posts/new')}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? 'Posting…' : 'Post'}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
